import React from 'react';

import {
  FormItem,
  Input,
  RadioGroup,
  Datepicker,
  TimeZonePicker,
} from '../../../../components';

import type { FormProps } from './CallTaskWizard';
import { validateSize } from '../../utils/validator';

interface ThirdStepWizardProps {
  values: FormProps;
}

const ThirdStepWizard: React.FC<ThirdStepWizardProps> = ({ values }) => {
  const getVoiceMailOptions = (fieldName: string) => {
    const baseOptions = [
      { label: 'Yes', id: true },
      { label: 'No', id: false },
    ];

    if (fieldName === 'leaveVoiceMail' && values.engageConversation === false) {
      return baseOptions.map(opt => ({
        ...opt,
        disabled: opt.id === false,
      }));
    }

    if (fieldName === 'engageConversation' && values.leaveVoiceMail === false) {
      return baseOptions.map(opt => ({
        ...opt,
        disabled: opt.id === false,
      }));
    }

    return baseOptions;
  };

  return (
    <div className="flex flex-col gap-y-2 max-w-5xl w-full mx-auto h-[55vh] overflow-hidden mt-10">
      <h3 className="text-3xl font-semibold">
        Batch Caller Task Configuration
      </h3>
      <p className="text-tiny text-secondary">Schedule calls and retries</p>
      <div className="flex flex-col gap-y-4 overflow-y-auto mt-4">
        <FormItem<FormProps>
          required
          labelClassName="text-tiny"
          name="maxCallDuration"
          label="Maximum call duration in minutes (max 60 min)"
          inline={false}
          validate={value => validateSize(value, 1, 60)}
        >
          <Input />
        </FormItem>
        <span className="text-tiny font-semibold">
          Voice mail (first two options cannot both be selected as 'No')
        </span>
        <div className="p-6 border border-border rounded-xl">
          <FormItem
            className="mt-4"
            labelClassName="text-tiny"
            name="engageConversation"
            label="Engage in conversation with people"
            inline={false}
            required
          >
            <RadioGroup
              direction="row"
              labelPosition="right"
              itemLabelClass="text-tiny"
              className="text-small text-secondary "
              itemClass="flex gap-x-2"
              options={getVoiceMailOptions('engageConversation')}
            />
          </FormItem>
          <FormItem
            className="mt-4"
            labelClassName="text-tiny"
            name="leaveVoiceMail"
            label="Leave voice mail"
            inline={false}
            required
          >
            <RadioGroup
              direction="row"
              labelPosition="right"
              itemLabelClass="text-tiny"
              className="text-small text-secondary"
              itemClass="flex gap-x-2"
              options={getVoiceMailOptions('leaveVoiceMail')}
            />
          </FormItem>
          <FormItem
            className="mt-4"
            labelClassName="text-tiny"
            name="keepTryingReachPerson"
            label="Keep trying to reach a person after left voicemail"
            inline={false}
            required
          >
            <RadioGroup
              direction="row"
              labelPosition="right"
              itemLabelClass="text-tiny"
              className="text-small text-secondary"
              itemClass="flex gap-x-2"
              options={getVoiceMailOptions('keepTryingReachPerson')}
            />
          </FormItem>
        </div>

        <FormItem<FormProps>
          required
          labelClassName="text-tiny"
          name="retriesNumber"
          label="Numbers of retries (1-10)"
          inline={false}
          validate={value => validateSize(value, 1, 10)}
        >
          <Input />
        </FormItem>
        <FormItem<FormProps>
          required
          labelClassName="text-tiny"
          name="retryInterval"
          label="Retry interval (1-60) minutes"
          inline={false}
          validate={value => validateSize(value, 1, 60)}
        >
          <Input />
        </FormItem>
        <FormItem<FormProps>
          required
          labelClassName="text-tiny"
          name="timezone"
          label="Timezone"
          inline={false}
        >
          <TimeZonePicker />
        </FormItem>
        <FormItem<FormProps>
          required
          labelClassName="text-tiny"
          name="taskDate"
          label="Task Date"
          inline={false}
        >
          <Datepicker format="MM/dd/yyyy" />
        </FormItem>
        <div className="flex gap-x-6">
          <FormItem<FormProps>
            required
            labelClassName="text-tiny"
            name="startTime"
            label="Start Time"
            inline={false}
          >
            <Datepicker format="HH:mm" disableCalendar />
          </FormItem>
          <FormItem<FormProps>
            required
            labelClassName="text-tiny"
            name="endTime"
            label="End Time"
            inline={false}
          >
            <Datepicker format="HH:mm" disableCalendar />
          </FormItem>
        </div>
      </div>
    </div>
  );
};

export default ThirdStepWizard;
